import { FORBIDDEN, UNPROCESSABLE_ENTITY } from '@mm/constants/status-codes';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import PromoterController from '../controllers/PromoterController';
import commonHandler from '../errorHandlers/common-handler';
import { PromoterModel } from '../model/PromoterModel';
import modalActions from '../modules/modalsState/actions';
import { StoreState } from '../store-interfaces';
import chatbotCRUDActions from './ChatbotCRUDActions';
import ConnectedPageCRUDActions from './ConnectedPageCRUDActions';
import CrudActionContainer from './CrudActionContainer';
import { showFacebookConnectModal } from './networkActions';

const MATCH_CATCH_ALL_EXCEPTION_CODE = 1310;
const NOT_UNIQ_KEYWORDS_EXCEPTION_CODE = 1311;

const customErrorHandler = dispatch => async error => {
  if (error.status === FORBIDDEN) {
    dispatch(
      showFacebookConnectModal(
        'Please give us permissions to your account',
        false,
        'ALL_PERMISSIONS',
      ),
    );
    dispatch(ConnectedPageCRUDActions.unbusy());
    dispatch(promoterCRUDActions.unbusy());
  } else if (error.status === UNPROCESSABLE_ENTITY) {
    dispatch(promoterCRUDActions.unbusy());
    await commonHandler(dispatch)(error);
  } else {
    await commonHandler(dispatch)(error);
  }
};

class PromoterCRUDActions extends CrudActionContainer {
  retrieveCatchAllAction: string;
  retrievedCatchAllAction: string;
  clearCatchAllAction: string;

  constructor() {
    super('PROMOTER');
    this.retrieveCatchAllAction = this.makeActionKey('RETRIEVE_CATCH_ALL');
    this.retrievedCatchAllAction = this.makeActionKey('RETRIEVED_CATCH_ALL');
    this.clearCatchAllAction = this.makeActionKey('CLEAR_CATCH_ALL');
  }

  create(promoter: PromoterModel) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.createAction));

        const createdPromoter = await PromoterController.postPromoter(promoter);
        dispatch(this.retrieved(createdPromoter));

        return createdPromoter;
      } catch (err) {
        await customErrorHandler(dispatch)(err);
        throw err;
      }
    };
  }

  retrieveAll(botId: number, kind: string) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.retrieveAllAction));

        const response = await PromoterController.getAll(botId, kind);
        dispatch(this.retrievedAll(response));

        return response;
      } catch (err) {
        await customErrorHandler(dispatch)(err);
        throw err;
      }
    };
  }

  retrievePromoterById(botId: number, promoterId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAction));
      try {
        const response = await PromoterController.getPromoterById(
          botId,
          promoterId,
        );
        dispatch(this.retrieved(response));
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  retrieveCatchAllGuard(botId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await PromoterController.getCatchAllGuard(botId);
        dispatch(this.retrievedCatchAll(response));
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  clearCatchAll() {
    return (dispatch: Dispatch<StoreState>) =>
      dispatch(this.clearCatchAllGuard());
  }

  update(
    botId: number,
    promoter: Partial<PromoterModel>,
    setBusy: boolean = true,
    catchManually?: boolean,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        if (setBusy) {
          dispatch(this.busy(this.updateAction));
        }
        if (promoter.type === 'customer_chat') {
          dispatch(
            chatbotCRUDActions.updateSetupStepsRemaining(
              'chatWidgetUpdate',
              'completed',
            ),
          );
        }

        const response = await PromoterController.putPromoter(botId, promoter);

        dispatch(this.updateLocal(response));
        dispatch(this.retrieved(response));

        return response;
      } catch (e) {
        let catchAllException:
          | { ids: number[]; message: string; error_code: number }
          | undefined;

        let outOfCreditsException:
          | { ids: number[]; message: string; error_code: number }
          | undefined;

        let keywordException: { message: string; error_code: number };

        if (e instanceof Response && e.status === 422) {
          const err = await e.clone().json();

          outOfCreditsException = err.errors?.find(error =>
            error?.toLowerCase?.().includes('you are out of credits'),
          );

          catchAllException = err.errors?.find(
            error => MATCH_CATCH_ALL_EXCEPTION_CODE === error?.error_code,
          );

          keywordException = err.errors?.find(
            error => NOT_UNIQ_KEYWORDS_EXCEPTION_CODE === error?.error_code,
          );

          if (outOfCreditsException) {
            dispatch(this.unbusy());
            throw outOfCreditsException;
          }

          if (keywordException) {
            dispatch(
              modalActions.openPrettyErrorModal({
                title: 'Another story reaction is active',
                errorMessage: keywordException.message,
              }),
            );
            dispatch(this.unbusy());
            return;
          }

          if (catchAllException && catchAllException.ids.length) {
            dispatch(
              modalActions.openGeneralConfirmModal({
                headerText:
                  promoter.type === 'instagram_story_reaction'
                    ? 'Another story reaction is active'
                    : promoter.type === 'instagram_catch_all'
                    ? 'Another Instagram Engagements Autoresponder is active'
                    : 'Another Facebook Engagements Autoresponder is active',
                messageText: catchAllException.message,
                onCloseHandler: async (ok: boolean) => {
                  if (ok) {
                    const chain = catchAllException?.ids.map(id =>
                      dispatch(
                        this.update(botId, {
                          botId,
                          id,
                          active: false,
                        }),
                      ),
                    );

                    if (chain?.length) {
                      await Promise.all(chain);
                    }

                    await dispatch(this.update(botId, promoter));
                  }
                },
                okButtonText: 'Deactivate',
              }),
            );
            dispatch(this.unbusy());
            return;
          }
        }

        await customErrorHandler(dispatch)(e);

        if (catchManually) {
          throw e;
        }
      }
    };
  }

  delete(promoter: Pick<PromoterModel, 'botId' | 'id'>) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.deleteAction));

        await PromoterController.deletePromoter(promoter);
        dispatch(this.deleteLocal(promoter));
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  moveToCampaign(
    ...args: Parameters<typeof PromoterController.moveToCampaign>
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy());

        const response = await PromoterController.moveToCampaign(...args);

        dispatch(this.updateLocal(response));
        dispatch(this.retrieved(response));

        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  retrievedCatchAll(entity: PromoterModel) {
    return {
      type: this.retrievedCatchAllAction,
      entity,
    };
  }

  clearCatchAllGuard() {
    return {
      type: this.clearCatchAllAction,
    };
  }
}

const promoterCRUDActions = new PromoterCRUDActions();

export default promoterCRUDActions;

export const usePromoterCRUDActions = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  return useMemo(
    () => ({
      create: (promoter: PromoterModel) =>
        dispatch(promoterCRUDActions.create(promoter)),
      retrieveAll: (botId: number, kind: string) =>
        dispatch(promoterCRUDActions.retrieveAll(botId, kind)),
      retrievePromoterById: (botId: number, promoterId: number) =>
        dispatch(promoterCRUDActions.retrievePromoterById(botId, promoterId)),
      retrieveCatchAllGuard: (botId: number) =>
        dispatch(promoterCRUDActions.retrieveCatchAllGuard(botId)),
      clearCatchAll: () => dispatch(promoterCRUDActions.clearCatchAll()),
      update: (...args: Parameters<typeof promoterCRUDActions.update>) =>
        dispatch(promoterCRUDActions.update(...args)),
      moveToCampaign: (
        ...args: Parameters<typeof promoterCRUDActions.moveToCampaign>
      ) => dispatch(promoterCRUDActions.moveToCampaign(...args)),
      delete: (promoter: Pick<PromoterModel, 'botId' | 'id'>) =>
        dispatch(promoterCRUDActions.delete(promoter)),
      retrievedCatchAll: (entity: PromoterModel) =>
        dispatch(promoterCRUDActions.retrievedCatchAll(entity)),
      clearCatchAllGuard: () =>
        dispatch(promoterCRUDActions.clearCatchAllGuard()),
    }),
    [dispatch],
  );
};
